"use client"

import { useState, useEffect } from "react"

import Layout from "../components/Layout"
import Card from "../components/Card"
import { Users, Package, Activity, TrendingUp, BarChart2  } from "react-feather"

const Analytics = () => {
  const [userStats, setUserStats] = useState({
    pharmacies: 0,
    suppliers: 0,
    customers: 0,
    admins: 0,
    total: 0,
    pending: 0,
    active: 0,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [chartData, setChartData] = useState([])
  const [error, setError] = useState(null)
  const API_BASE_URL = 'https://api.tenarx.net';
  useEffect(() => {
    fetchUserStatistics()
  }, [])

  const fetchUserStatistics = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("userToken")
      const res = await fetch(`${API_BASE_URL}/api/analytics/user-stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      })
      const data = await res.json()
      console.log(data)
      if (res.ok && data.success) {
        setUserStats(data.data)
        setChartData([
          { name: "Pharmacies", value: data.data.pharmacys, color: "#3B82F6" },
          { name: "Suppliers", value: data.data.suppliers, color: "#10B981" },
          { name: "Customers", value: data.data.customers, color: "#F59E0B" },
          { name: "Admins", value: data.data.admins, color: "#EF4444" },
        ])
      } else {
        setError(data.message || "Failed to fetch analytics data")
      }
    } catch (error) {
      setError("Error fetching user statistics")
    } finally {
      setIsLoading(false)
    }
  }

  const StatCard = ({ title, value, icon: Icon, color, percentage }) => {
    return (
      <Card className="flex flex-col">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <h3 className="text-2xl font-bold mt-1">
              {isLoading ? <div className="animate-pulse bg-gray-200 h-8 w-16 rounded"></div> : value}
            </h3>
            {percentage && <p className="text-xs text-gray-600 mt-1">{percentage}% of total users</p>}
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </Card>
    )
  }

  const SimpleBarChart = ({ data }) => {
    const maxValue = Math.max(...data.map((item) => item.value))

    return (
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">{item.name}</span>
              <span className="text-sm text-gray-600">{item.value}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="h-3 rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${maxValue ? (item.value / maxValue) * 100 : 0}%`,
                  backgroundColor: item.color,
                }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const SimplePieChart = ({ data }) => {
    const total = data.reduce((sum, item) => sum + item.value, 0)
    let cumulativePercentage = 0

    return (
      <div className="flex items-center justify-center">
        <div className="relative w-48 h-48">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#f3f4f6" strokeWidth="8" />
            {data.map((item, index) => {
              const percentage = total ? (item.value / total) * 100 : 0
              const strokeDasharray = `${percentage * 2.51} ${251 - percentage * 2.51}`
              const strokeDashoffset = -cumulativePercentage * 2.51
              cumulativePercentage += percentage

              return (
                <circle
                  key={index}
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke={item.color}
                  strokeWidth="8"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-500 ease-out"
                />
              )
            })}
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold">{total}</div>
              <div className="text-sm text-gray-500">Total Users</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Layout title="Analytics Dashboard">
      <div className="space-y-6">
        {error && <div className="text-red-500 text-center">{error}</div>}
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Pharmacies"
            value={userStats.pharmacys}
            icon={Package}
            color="bg-blue-500"
            percentage={userStats.total > 0 ? Math.round((userStats.pharmacys / userStats.total) * 100) : 0}
          />
          <StatCard
            title="Total Suppliers"
            value={userStats.suppliers}
            icon={TrendingUp}
            color="bg-green-500"
            percentage={userStats.total > 0 ? Math.round((userStats.suppliers / userStats.total) * 100) : 0}
          />
          <StatCard
            title="Total Customers"
            value={userStats.customers}
            icon={Users}
            color="bg-yellow-500"
            percentage={userStats.total > 0 ? Math.round((userStats.customers / userStats.total) * 100) : 0}
          />
          <StatCard title="Total Users" value={userStats.total} icon={Activity} color="bg-purple-500" />
        </div>

        {/* Account Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="Active Accounts"
            value={userStats.active}
            icon={Users}
            color="bg-green-600"
            percentage={userStats.total > 0 ? Math.round((userStats.active / userStats.total) * 100) : 0}
          />
          <StatCard
            title="Pending Approval"
            value={userStats.pending}
            icon={Activity}
            color="bg-orange-500"
            percentage={userStats.total > 0 ? Math.round((userStats.pending / userStats.total) * 100) : 0}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <Card title="User Distribution by Role" className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2  className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">Users by Role</span>
            </div>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <SimpleBarChart data={chartData} />
            )}
          </Card>

          {/* Pie Chart */}
          <Card title="User Role Distribution" className="space-y-4">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <SimplePieChart data={chartData} />
                <div className="grid grid-cols-2 gap-2">
                  {chartData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                      <span className="text-xs text-gray-600">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Recent Activity Summary */}
        <Card title="Quick Insights" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800">Most Active Role</h4>
              <p className="text-blue-600">
                {isLoading
                  ? "Loading..."
                  : chartData.length > 0
                    ? chartData.reduce((prev, current) => (prev.value > current.value ? prev : current)).name
                    : "N/A"}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800">Approval Rate</h4>
              <p className="text-green-600">
                {isLoading
                  ? "Loading..."
                  : userStats.total > 0
                    ? `${Math.round((userStats.active / userStats.total) * 100)}%`
                    : "0%"}
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="font-semibold text-yellow-800">Growth Trend</h4>
              <p className="text-yellow-600">{isLoading ? "Loading..." : "Steady Growth"}</p>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  )
}

export default Analytics
